import { useAtom } from "jotai";
import toast from "react-hot-toast";
import { Modal, Button, Row, Col, Form, Spinner } from "react-bootstrap";
import axiosInstance from "helpers/api";
import { useParams } from "react-router-dom";
import { convertToISO8601 } from "helpers/utils";
import SearchBox from "components/common/SearchBox";
import { Fragment, useEffect, useState } from "react";
import MeetingCard from "components/cards/MeetingCard";
import NoDataFound from "components/common/NoDataFound";
import { useForm, SubmitHandler } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { leadDetailsMeetingsAtom } from "jotai/LeadsJotaiInstance";
import { faFilter, faPlus, faSearch, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";

type Inputs = {
  end: string;
  topic: string;
  start: string;
  location: string;
  description: string;
};

const LeadDetailsMeeting = ({ meetings, handleSearchMeeting }: any) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<Inputs>();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [meetingList, setMeetingList] = useState([]);
  const [minDateTime, setMinDateTime] = useState("");
  const [, setLeadDetailsMeeting] = useAtom(leadDetailsMeetingsAtom);
  let { id } = useParams();

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(`/leads/${id}/meetings`, {
        meeting_name: data.topic,
        meeting_start: convertToISO8601(data.start),
        meeting_end: convertToISO8601(data.end),
        meeting_location: data.location,
      });
      setMeetingList(response.data.data.meetings);
      setLeadDetailsMeeting(response.data.data.meetings);
      toast.success(response.data.message);
      reset();
      setOpen(false);
    } catch (error: any) {
      toast.error(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setMeetingList(meetings);
  }, [meetings]);

  useEffect(() => {
    const now = new Date();
    const localISOTime = new Date(
      now.getTime() - now.getTimezoneOffset() * 60000
    )
      .toISOString()
      .slice(0, 16);
    setMinDateTime(localISOTime);
  }, []);

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const selectedDateTime = e.target.value;
    const datePart = selectedDateTime.split("T")[0];
    const timePart = selectedDateTime.split("T")[1];

    if (datePart && timePart) {
      const [hour, minute] = timePart.split(":")
      const updatedHour = parseInt(hour) + 1;
      const updatedTime = `${updatedHour.toString().padStart(2, "0")}:${minute}`;
      const updatedDateTime = `${datePart}T${updatedTime}`;
      setValue("end", updatedDateTime);
    }
  };

  return (
    <Fragment>
      <h2 className="mb-4">Meeting</h2>
      <Row className="align-items-center g-3 justify-content-between mb-3">
        <Col xs="12" sm="auto">
          <SearchBox
            placeholder="Search meeting"
            className="w-100"
            onChange={handleSearchMeeting}
            prepend={<FontAwesomeIcon icon={faSearch} className="text-muted" />}
          />
        </Col>
        <Col xs="auto" className="flex-md-grow-1">
          <div className="d-flex">
            <p className="mb-0 fs-9 text-body-tertiary fw-bold">
              <FontAwesomeIcon
                icon={faFilter}
                className="me-1 fw-extra-bold fs-10"
              />
              {meetingList.length} {meetingList.length <= 1 ? "Meeting" : "Meetings"}
            </p>
          </div>
        </Col>
        <Col xs="auto">
          <Button
            variant="primary"
            onClick={() => setOpen(true)}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Meeting
          </Button>
        </Col>
      </Row>
      <Row className="g-3">
        {meetingList.length > 0 ? (
          meetingList.map((meeting: any) => (
            <Col xxl={6} key={meeting.id}>
              <MeetingCard meeting={meeting} />
            </Col>
          ))
        ) : (
          <NoDataFound type="Meetings" customClass="mt-lg-10" />
        )}
      </Row>

      <Modal
        show={open}
        onHide={() => setOpen(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-primary" />
            Add Meeting
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3">
              <Form.Group>
                <Form.Label className="form-label-header mb-2">
                  Topic
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Topic"
                  {...register("topic", {
                    required: true,
                    maxLength: 100,
                  })}
                />
              </Form.Group>
              {errors.topic?.type === "required" && (
                <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                  Topic is required
                </p>
              )}
            </div>

            <div className="mb-3">
              <Form.Group>
                <Form.Label className="form-label-header mb-2">
                  Description
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter Description"
                  {...register("description", {
                    required: true,
                    maxLength: 500,
                  })}
                />
              </Form.Group>
              {errors.description?.type === "required" && (
                <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                  Description is required
                </p>
              )}
            </div>

            <div className="mb-3">
              <Form.Group>
                <Form.Label className="form-label-header mb-2">
                  Location
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Location"
                  {...register("location")}
                />
              </Form.Group>
            </div>

            <Row className="g-3">
              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="form-label-header mb-2">
                    Start Time
                  </Form.Label>
                  <Form.Control
                    min={minDateTime}
                    type="datetime-local"
                    placeholder="Enter Start Time"
                    {...register("start", {
                      required: true,
                      maxLength: 100,
                    })}
                    onChange={(e) => {
                      // Call the custom handler and ensure React Hook Form registers the change
                      handleStartTimeChange(e);
                      register("start").onChange(e); // This ensures React Hook Form handles the change
                    }}
                  />
                </Form.Group>
                {errors.start?.type === "required" && (
                  <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                    Start Time is required
                  </p>
                )}
              </Col>

              <Col lg={6}>
                <Form.Group>
                  <Form.Label className="form-label-header mb-2">
                    End Time
                  </Form.Label>
                  <Form.Control
                    min={minDateTime}
                    type="datetime-local"
                    placeholder="Enter End Time"
                    {...register("end", {
                      required: true,
                      maxLength: 100,
                    })}
                  />
                </Form.Group>
                {errors.end?.type === "required" && (
                  <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                    End Time is required
                  </p>
                )}
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div></div>
          <div className="d-flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
            >
              {loading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
              ) : (
                'Add Meeting'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default LeadDetailsMeeting;
